import React, { useState, useEffect } from "react";
import {
  Box,
  Input,
  Button,
  Heading,
  FormControl,
  FormLabel,
  VStack,
  Text,
  FormErrorMessage,
  Select,
  Checkbox,
  Textarea,
  HStack,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle, faTable } from "@fortawesome/free-solid-svg-icons";

const QuestionnaireForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load questionnaire from local storage
    try {
      const questionnaires = JSON.parse(
        localStorage.getItem("questionnaires") || "[]"
      );
      const q = questionnaires.find((q) => q.id === id);
      if (q) {
        setQuestionnaire(q);
        const initialFormData = {};
        q.fields.forEach((field) => {
          initialFormData[field.name] = field.type === "checkbox" ? [] : "";
        });
        setFormData(initialFormData);
      } else {
        alert("ไม่พบแบบสอบถาม");
        navigate("/questionnaire-list");
      }
    } catch (error) {
      console.error("Error loading questionnaire:", error);
      navigate("/questionnaire-list");
    }
  }, [id, navigate]);

  const handleInputChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    const field = questionnaire.fields.find((f) => f.name === name);

    if (field.type === "text" || field.type === "textarea") {
      if (!value.trim()) {
        newErrors[name] = "กรุณากรอกข้อมูล";
      } else {
        delete newErrors[name];
      }
    } else if (field.type === "select") {
      if (!value) {
        newErrors[name] = "กรุณาเลือกตัวเลือก";
      } else {
        delete newErrors[name];
      }
    } else if (field.type === "checkbox") {
      if (value.length === 0) {
        newErrors[name] = "กรุณาเลือกอย่างน้อย 1 ตัวเลือก";
      } else if (value.length > field.maxSelections) {
        newErrors[
          name
        ] = `สามารถเลือกได้สูงสุด ${field.maxSelections} ตัวเลือก`;
      } else {
        delete newErrors[name];
      }
    }

    setErrors(newErrors);
  };

  const isFormValid = () => {
    const newErrors = {};
    questionnaire.fields.forEach((field) => {
      const value = formData[field.name];
      if (field.type === "text" || field.type === "textarea") {
        if (!value.trim()) {
          newErrors[field.name] = "กรุณากรอกข้อมูล";
        }
      } else if (field.type === "select") {
        if (!value) {
          newErrors[field.name] = "กรุณาเลือกตัวเลือก";
        }
      } else if (field.type === "checkbox") {
        if (value.length === 0) {
          newErrors[field.name] = "กรุณาเลือกอย่างน้อย 1 ตัวเลือก";
        } else if (value.length > field.maxSelections) {
          newErrors[
            field.name
          ] = `สามารถเลือกได้สูงสุด ${field.maxSelections} ตัวเลือก`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง");
      return;
    }

    try {
      const response = {
        questionnaireId: id,
        responses: formData,
        submittedAt: new Date().toISOString(),
      };
      const existingResponses = JSON.parse(
        localStorage.getItem("responses") || "[]"
      );
      existingResponses.push(response);
      localStorage.setItem("responses", JSON.stringify(existingResponses));
      alert("ส่งแบบสอบถามสำเร็จ!");
      navigate("/questionnaire-list");
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกคำตอบ: " + error.message);
    }
  };

  if (!questionnaire) return null;

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Box
        mb={5}
        borderRadius="full"
        w="100%"
        bgColor="iPink.500"
        color="white"
        p={2}
      >
        <HStack spacing={5} ml={5}>
          <FontAwesomeIcon icon={faTable} color="white" size="xl" />
          <Heading size="lg" fontWeight="normal">
            แบบสอบถาม : 
          </Heading>
        </HStack>
      </Box>

      <Box m={10}>
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <Box
            mb={5}
            borderRadius="full"
            w="100%"
            bgColor="iTeal.500"
            color="white"
            p={2}
          >
            <HStack spacing={5} ml={5}>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                color="white"
                size="xl"
              />
              <Heading size="md" fontWeight="normal">
                {questionnaire.title}
              </Heading>
            </HStack>
          </Box>
          <VStack spacing={6} p={5}>
            {questionnaire.fields.map((field) => (
              <FormControl
                key={field.name}
                isRequired
                isInvalid={!!errors[field.name]}
              >
                <FormLabel fontSize="xl" color="gray">
                  {field.label}{" "}
                  <Text as="span" color="red">
                    {" "}
                    *
                  </Text>
                  {field.type === "checkbox" && (
                    <Text as="span" fontSize="sm" ml={2}>
                      (เลือกได้สูงสุด {field.maxSelections} ข้อ)
                    </Text>
                  )}
                </FormLabel>
                {field.type === "text" && (
                  <Input
                    value={formData[field.name] || ""}
                    onChange={(e) =>
                      handleInputChange(field.name, e.target.value)
                    }
                    colorScheme="iTeal"
                  />
                )}
                {field.type === "textarea" && (
                  <Textarea
                    value={formData[field.name] || ""}
                    onChange={(e) =>
                      handleInputChange(field.name, e.target.value)
                    }
                    colorScheme="iTeal"
                  />
                )}
                {field.type === "select" && (
                  <Select
                    value={formData[field.name] || ""}
                    onChange={(e) =>
                      handleInputChange(field.name, e.target.value)
                    }
                    placeholder="เลือกตัวเลือก"
                    colorScheme="iTeal"
                  >
                    {field.options.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                )}
                {field.type === "checkbox" && (
                  <VStack align="start">
                    {field.options.map((option, index) => (
                      <Checkbox
                        key={index}
                        isChecked={formData[field.name]?.includes(option)}
                        onChange={(e) => {
                          const current = formData[field.name] || [];
                          const updated = e.target.checked
                            ? [...current, option]
                            : current.filter((opt) => opt !== option);
                          handleInputChange(field.name, updated);
                        }}
                        colorScheme="iTeal"
                      >
                        {option}
                      </Checkbox>
                    ))}
                  </VStack>
                )}
                <FormErrorMessage>{errors[field.name]}</FormErrorMessage>
              </FormControl>
            ))}
          </VStack>
        </Box>

        <Box p={5} m={5}>
          <Button colorScheme="blue" onClick={handleSubmit}>
            ส่งแบบสอบถาม
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionnaireForm;
